import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { matchCommand } from "@/lib/commands";
import { scrollToId, scrollToTop, scrollBy } from "@/lib/scroll";
import { speechBus, stopAllSpeech } from "@/lib/speech";
import { links, cases } from "@/config";

const VoiceCtx = createContext(null);
export const useVoice = () => useContext(VoiceCtx);

export const LANGS = [
  { code: "en-IN", label: "EN" },
  { code: "hi-IN", label: "हिं" },
  { code: "ta-IN", label: "த" },
];

const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

const FEEDBACK = {
  "nav:home": "Taking you home.",
  "nav:about": "Opening the attorney's file.",
  "nav:cases": "Showing the cases.",
  "nav:evidence": "Showing the evidence.",
  "nav:tools": "Showing the tools.",
  "nav:languages": "Three languages, coming up.",
  "nav:credentials": "Pulling the credentials.",
  "nav:terms": "Reading the terms.",
  "nav:contact": "Opening contact.",
  "case:open": "Opening it.",
  "case:next": "Next case.",
  "case:prev": "Previous case.",
  close: "Closing.",
  "chat:open": "Opening the chat.",
  "chat:close": "Closing the chat.",
  voicemail: "Recording booth is ready.",
  "link:linkedin": "Opening LinkedIn.",
  "link:github": "Opening GitHub.",
  opening: "Playing the opening statement.",
  transcript: "Here's the transcript.",
  play: "Playing.",
  stop: "Stopped.",
  help: "Here's what you can say.",
  top: "Back to the top.",
  "scroll:up": "Scrolling up.",
  "scroll:down": "Scrolling down.",
};

export function VoiceProvider({ children }) {
  const [lang, setLang] = useState("en-IN");
  const [micState, setMicState] = useState(SR ? "idle" : "unsupported");
  const [caption, setCaption] = useState("");
  const [feedback, setFeedback] = useState(null); // { text, tone: 'ok'|'warn' }
  const [micDenied, setMicDenied] = useState(false);

  const [chatOpen, setChatOpen] = useState(false);
  const [caseIndex, setCaseIndex] = useState(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [voicemailOpen, setVoicemailOpen] = useState(false);

  const recRef = useRef(null);
  const retryRef = useRef([]);
  const feedbackTimer = useRef(null);
  const players = useRef({}); // { opening: {play, stop}, case: {play, stop} }

  const registerPlayer = useCallback((key, handlers) => {
    players.current[key] = handlers;
    return () => { if (players.current[key] === handlers) delete players.current[key]; };
  }, []);

  const say = useCallback((text, tone = "ok") => {
    clearTimeout(feedbackTimer.current);
    setFeedback({ text, tone });
    feedbackTimer.current = setTimeout(() => setFeedback(null), 3600);
  }, []);

  const openCase = useCallback((i) => {
    const n = ((i % cases.items.length) + cases.items.length) % cases.items.length;
    setChatOpen(false);
    setCaseIndex(n);
  }, []);

  const execute = useCallback((cmd, heard) => {
    const { intent, arg } = cmd;
    const act = {
      "case:open": () => { scrollToId("cases"); openCase(arg - 1); },
      "case:next": () => openCase(caseIndex === null ? 0 : caseIndex + 1),
      "case:prev": () => openCase(caseIndex === null ? cases.items.length - 1 : caseIndex - 1),
      close: () => { setCaseIndex(null); setChatOpen(false); setTranscriptOpen(false); setHelpOpen(false); setVoicemailOpen(false); },
      "chat:open": () => { setCaseIndex(null); setChatOpen(true); },
      "chat:close": () => setChatOpen(false),
      voicemail: () => { scrollToId("contact"); setVoicemailOpen(true); },
      "link:linkedin": () => window.open(links.linkedin, "_blank", "noopener"),
      "link:github": () => window.open(links.github, "_blank", "noopener"),
      opening: () => { scrollToTop(); players.current.opening?.play(); },
      transcript: () => { scrollToTop(); setTranscriptOpen(true); },
      play: () => (caseIndex !== null ? players.current.case?.play() : players.current.opening?.play()),
      stop: () => { stopAllSpeech(); players.current.case?.stop?.(); players.current.opening?.stop?.(); },
      help: () => setHelpOpen(true),
      top: scrollToTop,
      "scroll:up": () => scrollBy(-1),
      "scroll:down": () => scrollBy(1),
    }[intent];
    if (act) act();
    else if (intent.startsWith("nav:")) { setCaseIndex(null); scrollToId(intent.slice(4)); }
    say(`Heard "${heard}". ${FEEDBACK[intent] || "Done."}`);
  }, [caseIndex, openCase, say]);

  const stopListening = useCallback(() => {
    retryRef.current = [];
    try { recRef.current?.abort(); } catch { /* noop */ }
    recRef.current = null;
    setCaption("");
    setMicState((s) => (s === "unsupported" ? s : "idle"));
  }, []);

  const handleTranscript = useCallback((alts) => {
    setMicState("processing");
    let best = null, heard = alts[0];
    for (const t of alts) {
      const m = matchCommand(t);
      if (m && (!best || m.score > best.score)) { best = m; heard = t; }
    }
    setTimeout(() => {
      if (best) {
        execute(best, heard);
        setMicState("idle");
        setCaption("");
        return;
      }
      const nextLang = retryRef.current.shift();
      if (nextLang) { startRec(nextLang); return; }
      setMicState("idle");
      setCaption("");
      say("Didn't catch that. Try \"show cases\", \"open case 2\", or ask Andrew.", "warn");
    }, 350);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [execute, say]);

  const startRec = useCallback((recLang) => {
    if (!SR) return;
    try { recRef.current?.abort(); } catch { /* noop */ }
    const rec = new SR();
    rec.lang = recLang;
    rec.interimResults = true;
    rec.continuous = false;
    rec.maxAlternatives = 4;
    let gotFinal = false;
    rec.onstart = () => { setMicState("listening"); setCaption(""); };
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) {
          gotFinal = true;
          const alts = Array.from(r).map((a) => a.transcript.trim()).filter(Boolean);
          setCaption(alts[0] || "");
          handleTranscript(alts);
        } else interim += r[0].transcript;
      }
      if (!gotFinal && interim) setCaption(interim);
    };
    rec.onerror = (e) => {
      if (e.error === "not-allowed" || e.error === "service-not-allowed") {
        setMicDenied(true);
        setMicState("error");
        say("Microphone permission was denied. Allow it in your browser, or type a command.", "warn");
      } else if (e.error === "no-speech") {
        setMicState("idle");
      } else if (e.error !== "aborted") {
        setMicState("error");
        say("Speech recognition hit a snag. Tap the mic and try again.", "warn");
      }
      setCaption("");
    };
    rec.onend = () => {
      if (recRef.current === rec && !gotFinal) {
        setMicState((s) => (s === "listening" ? "idle" : s));
      }
    };
    recRef.current = rec;
    try { rec.start(); } catch { setMicState("idle"); }
  }, [handleTranscript, say]);

  const startListening = useCallback(() => {
    if (!SR) return;
    stopAllSpeech();
    players.current.case?.stop?.();
    players.current.opening?.stop?.();
    retryRef.current = LANGS.map((l) => l.code).filter((c) => c !== lang);
    startRec(lang);
  }, [lang, startRec]);

  const toggleListening = useCallback(() => {
    if (micState === "listening") stopListening();
    else startListening();
  }, [micState, startListening, stopListening]);

  /** For the text command box (no speech support) */
  const runText = useCallback((text) => {
    const m = matchCommand(text);
    if (m) execute(m, text);
    else say("Didn't catch that. Try \"show cases\", \"open case 2\", or ask Andrew.", "warn");
    return !!m;
  }, [execute, say]);

  // Never hear ourselves: pause recognition while the site speaks
  useEffect(() => {
    const onStart = () => { if (recRef.current) stopListening(); };
    speechBus.addEventListener("start", onStart);
    return () => speechBus.removeEventListener("start", onStart);
  }, [stopListening]);

  const value = useMemo(() => ({
    supported: !!SR, lang, setLang, micState, caption, feedback, micDenied,
    toggleListening, stopListening, runText,
    chatOpen, setChatOpen, caseIndex, setCaseIndex, openCase,
    transcriptOpen, setTranscriptOpen, helpOpen, setHelpOpen, voicemailOpen, setVoicemailOpen,
    registerPlayer, say,
  }), [lang, micState, caption, feedback, micDenied, toggleListening, stopListening, runText, chatOpen, caseIndex, openCase, transcriptOpen, helpOpen, voicemailOpen, registerPlayer, say]);

  return <VoiceCtx.Provider value={value}>{children}</VoiceCtx.Provider>;
}
